import { useEffect } from 'react';
import type { VFC } from 'react';

import { Route, Switch, useLocation } from 'react-router-dom';
import { Provider } from '@vgtv/api-client';

import { CategoryPage } from './pages/CategoryPage';
import { AssetPage } from './pages/AssetPage/AssetPage';
import { TagPage } from './pages/TagPage';
import { getRoute, routeNames } from './shared/helpers/routes.js';
import { StoryPage } from './pages/StoryPage';
import { CustomPage } from './pages/CustomPage';
import { usePreviousValue } from './components/utils/usePreviousValue';
import { NotFoundPage } from './pages/NotFoundPage';
import { config } from './config.js';
import { usePlayerContext } from './player/PlayerContext';
import { isIOS } from './utils/device.js';
import { FavouritesPage } from './pages/FavouritesPage';
import { VendorProvider } from './components/VendorPovider/VendorPovider';
import { unleash } from './services/unleash.js';
import { usePipContext } from './player/PictureInPictureProvider';
import { SearchPage } from './pages/SearchPage';
import { PaywallPage } from './pages/PaywallPage';
import { LandingPage } from './pages/BigScreenLandingPage/BigScreenLandingPage';
import { getAssetIdFromLocation } from './helpers/getAssetIdFromLocation';

const Routes: VFC = () => {
  const location = useLocation();
  const previousLocation = usePreviousValue(location);
  const { stopPlayback, isCasting } = usePlayerContext();
  const { isPipEnabled } = usePipContext();

  useEffect(() => {
    // scroll to the top of the page
    if (location.pathname !== previousLocation.pathname) {
      window.scrollTo(0, 0);
    }

    const assetId = getAssetIdFromLocation({
      location,
    });
    const previousAssetId = getAssetIdFromLocation({
      location: previousLocation,
    });

    // reset the player when moving away from the asset page to fix player error 203000 on iOS
    if (
      assetId !== previousAssetId &&
      !assetId &&
      previousAssetId &&
      !isCasting &&
      !isPipEnabled &&
      isIOS
    ) {
      stopPlayback();
    }
  }, [stopPlayback, location, previousLocation]);

  return (
    <Switch>
      {config.provider === Provider.VGTV &&
        unleash.isEnabled('brandstudio') && (
          <Route
            path={getRoute(routeNames.category, { vendor: 'brandstudio' })}
          >
            <VendorProvider vendor="brandstudio">
              <CategoryPage />
            </VendorProvider>
          </Route>
        )}
      <Route path={getRoute(routeNames.category)}>
        <CategoryPage />
      </Route>
      <Route path={getRoute(routeNames.tag)} component={TagPage} />
      <Route path={getRoute(routeNames.story)} component={StoryPage} />
      {config.provider === Provider.VGTV && (
        <Route path={getRoute(routeNames.bigScreen)} component={LandingPage} />
      )}
      {config.provider === Provider.VGTV &&
        unleash.isEnabled('brandstudio') && (
          <Route path={getRoute(routeNames.asset, { vendor: 'brandstudio' })}>
            <VendorProvider vendor="brandstudio">
              <AssetPage />
            </VendorProvider>
          </Route>
        )}
      <Route path={getRoute(routeNames.asset)}>
        <AssetPage />
      </Route>
      {config.provider === Provider.VGTV && (
        <Route path={getRoute(routeNames.documentary)}>
          <AssetPage />
        </Route>
      )}
      {config.provider === Provider.VGTV && (
        <Route path={getRoute(routeNames.episode)}>
          <AssetPage />
        </Route>
      )}
      {config.provider === Provider.VGTV && (
        <Route path={getRoute(routeNames.series)}>
          <CategoryPage />
        </Route>
      )}
      {config.provider === Provider.VGTV && (
        <Route path={getRoute(routeNames.news)}>
          <AssetPage />
        </Route>
      )}
      {config.enableFavourites && (
        <Route
          path={getRoute(routeNames.favourites)}
          component={FavouritesPage}
        />
      )}
      {config.provider === Provider.VGTV && (
        <Route path={getRoute(routeNames.search)} component={SearchPage} />
      )}
      {config.provider === Provider.VGTV && (
        <Route path={getRoute(routeNames.paywall)} component={PaywallPage} />
      )}
      {/* the custom page route has to be last */}
      <Route path={getRoute(routeNames.custom)} component={CustomPage} />
      <Route exact path="/" component={CustomPage} />
      <Route path="*" component={NotFoundPage} />
    </Switch>
  );
};

export { Routes };
